import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from 'reactstrap';
import PageClean from '../default/PageClean';
import SweetAlert from 'react-bootstrap-sweetalert';
import Alert from '../../components/Alert';
import AltCheckbox from '../../components/AltCheckbox';
import {
  getCustomerPlan,
  updateCustomerPlan,
  createCustomerPlan,
  getPlanConfig,
  getExtract,
  getCustomerModuleVersions,
  updateCustomerModuleVersions,
} from '../../actions/ActionCustomer';
import { getByCode } from '../../actions/ActionVoucher';
import oClasses from './CustomerIndex.module.scss';

import CustomerFinancial from './CustomerPlanFinancial';

const CustomerPlanModal = ({
  fnOnClose = () => {},
  fnOnSubmit = () => {},
  nCustId,
}) => {
  const [bIsLoading, setIsLoading] = useState(true);
  const [bIsSubmitting, setIsSubmitting] = useState(false);
  const [cOpenModal, setOpenModal] = useState('');
  const [aErrors, setErrors] = useState([]);
  const [aSuccesses, setSuccesses] = useState([]);
  const [bInvalidVoucher, setInvalidVoucher] = useState('');
  const [bIsSavingModuleVersions, setIsSavingModuleVersions] = useState(false);

  const [cVoucherCode, setVoucherCode] = useState('');

  const [oSysConfig, setSysConfig] = useState({});
  const [aSysModules, setSysModules] = useState([]);
  const [oBill, setBill] = useState({});
  const [oCustomer, setCustomer] = useState({});

  const [oHiredModulesOld, setHiredModulesOld] = useState([]);
  const [oHiredUsersOld, setHiredUsersOld] = useState({});
  const [oHiredStorageOld, setHiredStorageOld] = useState({});

  const [aHiredModules, setHiredModules] = useState([]);
  const [aModuleVersions, setModuleVersions] = useState([]);
  const [nHiredUsers, setHiredUsers] = useState(0);
  const [nHiredStorage, setHiredStorage] = useState(0);

  const [bIsAnnualPlan, setIsAnnualPlan] = useState(false);
  const [oVoucher, setVoucher] = useState({});

  const nMinSpace = useMemo(() => Math.max(
    oSysConfig.min_space, // GB
    Math.ceil(oCustomer.cust_nused_space_quota_mb / 1024)
  ), [oCustomer, oSysConfig]);

  const nMinUsers = useMemo(() => Math.max(
    oSysConfig.default_total_users,
    oCustomer.nUserCount
  ), [oCustomer, oSysConfig]);

  const nDiffUsers = useMemo(() => {
    return nHiredUsers - oHiredUsersOld.prod_namount;
  }, [nHiredUsers, oHiredUsersOld]);

  const nDiffStorage = useMemo(() => {
    return nHiredStorage - oHiredStorageOld.prod_namount;
  }, [nHiredStorage, oHiredStorageOld]);

  const aDiffModules = useMemo(() => {
    const aDiff = []
    for (const oMod of aSysModules) {
      const bInNew = aHiredModules.includes(oMod.mod_nid);
      const bInOld = !!oHiredModulesOld[oMod.mod_nid];
      if (bInOld !== bInNew) {
        aDiff.push({ ...oMod, bAdded: bInNew });
      }
    }
    return aDiff;
  }, [aSysModules, aHiredModules, oHiredModulesOld]);

  const calcProductPrice = useCallback((nBaseUnitPrice, nAmount = 1, oVoucher = null, oProductOld = {}) => {
    const nOldAmount = oProductOld.prod_namount || 0;
    const nOldTotalPrice = oProductOld.prod_nprice || 0;

    if (nOldAmount === nAmount) return nOldTotalPrice;
    if (nOldAmount > nAmount) {
      // reduz pelo "preço unitário médio" dos produtos já contratados
      const nPrice = Math.ceil(nOldTotalPrice / nOldAmount);
      return nPrice * nAmount;
    }

    // produtos novos, pagar preço de acordo com plano atual
    const nVchDiscount = oVoucher.vch_ndiscount || 0;
    const nUnitPrice = Math.ceil(nBaseUnitPrice * (100 - nVchDiscount) / 100);

    const nDiffAmount = nAmount - nOldAmount;
    return nOldTotalPrice + nUnitPrice * nDiffAmount;
  }, []);

  const nPrice = useMemo(() => {
    if (bIsLoading) return 0;
    // se não selecionar nenhum módulo, plano não é válido: manter valor zero
    if (aHiredModules.length === 0) return 0;

    let nNewPrice = oSysConfig.software_basic_value || 0; // em centavos de real (1298 -> R$12,98)
    const nVchDiscount = oVoucher.vch_ndiscount || 0;
    const nVchPricePct = (100 - nVchDiscount) / 100;

    for(const nModId of aHiredModules) {
      const oModule = aSysModules.find(oMod => oMod.mod_nid === nModId);
      if (!oModule) continue;
      if (oHiredModulesOld[nModId]) {
        // módulo já contratado, mantém o preço/descontos já existentes
        nNewPrice += oHiredModulesOld[nModId].prod_nprice;
      } else {
        nNewPrice += oModule.mod_nvalue * nVchPricePct;
      }
    }

    let nUsersPrice = calcProductPrice(
      oSysConfig.value_per_user,
      nHiredUsers,
      oVoucher,
      oHiredUsersOld
    );
    console.log('users price', nUsersPrice);
    nNewPrice += nUsersPrice;

    let nStoragePrice = calcProductPrice(
      oSysConfig.value_per_gigabyte,
      nHiredStorage,
      oVoucher,
      oHiredStorageOld
    );
    console.log('storage price', nStoragePrice);
    nNewPrice += nStoragePrice;

    nNewPrice = Math.ceil(nNewPrice);

    if (bIsAnnualPlan) {
      nNewPrice *= 12;
      nNewPrice *= (100 - oSysConfig.discount_annual_plan) / 100;
      nNewPrice = Math.ceil(nNewPrice)
    }

    return nNewPrice;
  }, [
    bIsLoading,
    aHiredModules,
    oSysConfig,
    oVoucher,
    aSysModules,
    oHiredModulesOld,
    oHiredUsersOld,
    oHiredStorageOld,
    nDiffStorage,
    nDiffUsers,
    nHiredStorage,
    nHiredUsers,
    bIsAnnualPlan,
  ]);

  const bDisableHire = useMemo(() => {
    return bIsLoading ||
      aHiredModules.length === 0 ||
      (nDiffUsers === 0 && nDiffStorage === 0 && aDiffModules.length === 0);
  }, [bIsLoading, nDiffUsers, nDiffStorage, aDiffModules, aHiredModules]);

  const bHasVersionableModules = useMemo(() => {
    return aModuleVersions.some((oModuleVersion) => oModuleVersion.bIsVersionable);
  }, [aModuleVersions]);

  useEffect(() => {
    setIsAnnualPlan(oCustomer.cust_nplanid === oSysConfig.plan_nid_annual);
  }, [oCustomer, oSysConfig]);

  const loadCustomerPlan = useCallback(async (nCustId) => {
    const oResult = await getCustomerPlan(nCustId);

    setCustomer(oResult.oCustomer);

    const oHiredModules = oResult.aProducts
      .filter(p => p.prod_ctype === 'module')
      .reduce((oMods, oMod) => {
        oMods[oMod.mod_nid] = oMod;
        return oMods;
      }, {});

    setHiredModulesOld(oHiredModules);
    setHiredModules(Object.keys(oHiredModules).map(Number));

    const oEmpty = { prod_namount: 0 };

    const bEmptyModules = Object.keys(oHiredModules).length === 0;
    const { bIsTrial, nUserCount } = oResult.oCustomer;

    const oUsers = oResult.aProducts.find(p => p.prod_ctype === 'user') || oEmpty;
    setHiredUsersOld(bIsTrial && bEmptyModules ? oEmpty : oUsers);
    setHiredUsers((nUsers) => Math.max(nUsers, oUsers.prod_namount, nUserCount));

    const oSpace = oResult.aProducts.find(p => p.prod_ctype === 'space') || oEmpty;
    setHiredStorageOld(bIsTrial && bEmptyModules ? oEmpty : oSpace);
    setHiredStorage((nSpace) => Math.max(nSpace, oSpace.prod_namount));
  }, []);

  const loadConfig = useCallback(async () => {
    const oResult = await getPlanConfig();

    setSysConfig(oResult.oSysConfig);
    setSysModules(oResult.aModules);

    const nMinUsers = oResult.oSysConfig.default_total_users;
    setHiredUsers((nUsers) => Math.max(nUsers, nMinUsers));

    const nMinStorage = oResult.oSysConfig.min_space;
    setHiredStorage((nSpace) => Math.max(nSpace, nMinStorage));
  }, []);

  const loadCustomerModuleVersions = useCallback(async (nCustId) => {
    const oResult = await getCustomerModuleVersions(nCustId);

    setModuleVersions(oResult.aModules || []);
  }, []);

  useEffect(() => {
    Promise.all([
      loadCustomerPlan(nCustId),
      loadConfig(),
      loadCustomerModuleVersions(nCustId),
    ]).catch((oError) => {
      renderError(['Erro ao carregar dados.']);
    }).finally(() => setIsLoading(false));
    getExtract(nCustId)
      .then((oData) => setBill(oData))
      .catch(() => {});
  }, []);

  const clearError = (cError) => {
    setErrors((aPrv) => aPrv.filter((cErr) => cErr !== cError));
  }

  const clearSuccess = (cSuccess) => {
    setSuccesses((aPrv) => aPrv.filter((cSuc) => cSuc !== cSuccess));
  }

  const renderError = (aErrors) => {
    setErrors(aPrv => [...aPrv, ...aErrors]);
    // setTimeout(() => {
    //   window.scrollTo(0, 0);
    // }, 100);
  }

  const renderSuccess = (aMessages) => {
    setSuccesses(aPrv => [...aPrv, ...aMessages]);
  }

  const getBodyToSubmit = (oCustFinancial = {}) => {
    const oBody = {
      cust_cname: oCustFinancial.cust_cname,
      cust_cfantasyname: oCustFinancial.cust_cfantasyname,
      cust_ctype: oCustFinancial.cust_ctype,
      cust_ccnpj: oCustFinancial.cust_ccnpj,
      cust_cIE: oCustFinancial.cust_cIE,
      cust_cIM: oCustFinancial.cust_cIM,
      cust_etaxframework: oCustFinancial.cust_etaxframework,
      cust_ccpf: oCustFinancial.cust_ccpf,
      cust_cpostalcode: oCustFinancial.cust_cpostalcode,
      cust_cstreet: oCustFinancial.cust_cstreet,
      cust_cnumber: oCustFinancial.cust_cnumber,
      cust_ccomplement: oCustFinancial.cust_ccomplement,
      cust_cdistrict: oCustFinancial.cust_cdistrict,
      cust_ccity: oCustFinancial.cust_ccity,
      cust_cstate: oCustFinancial.cust_cstate,
      cust_ccountry: oCustFinancial.cust_ccountry,
      cust_cphone: oCustFinancial.cust_cphone,
      // campo é setado internamente na api, mas é obrigatório na requisição
      cust_cemail: oCustomer.wlpn_nid ? "a@a.com" : oCustFinancial.ca_cemail,
      cust_ntotalusers: nHiredUsers,
      cust_ntotalspace: nHiredStorage,
      cust_nplanid: bIsAnnualPlan
        ? oSysConfig.plan_nid_annual
        : oSysConfig.plan_nid_monthly,
      aModules: aHiredModules,
      sig_npayamount: nPrice,
      sig_bisannual: bIsAnnualPlan,
      sig_amodulesnid: aHiredModules,
      vch_ndiscount: oVoucher.vch_ndiscount || null,
      vch_nid: oVoucher.vch_nid || null,
      wlpn_nid: oCustomer.wlpn_nid
    };

    return oBody;
  }

  const savePlan = async (oCustFinancial, bThrowErrors) => {
    if (bIsSubmitting) return;

    const oBody = getBodyToSubmit(oCustFinancial);
    setIsSubmitting(true);
    try {
      if (oCustomer.bIsTrial) {
        await createCustomerPlan(nCustId, oBody);
      } else {
        await updateCustomerPlan(nCustId, oBody);
      }
      fnOnSubmit();
    } catch (oError) {
      setIsSubmitting(false);
      if (bThrowErrors) throw oError;
      renderError(oError.aMsgErrors);
    }
  };

  const handleVoucherChange = (evtChange) => {
    if (bInvalidVoucher) setInvalidVoucher(false);
    if (oVoucher) setVoucher({});
    setVoucherCode(evtChange.target.value);
  }

  const verifyVoucher = async () => {
    if (bIsSubmitting) return;
    if (!cVoucherCode) return setInvalidVoucher(true);
    setIsSubmitting(true);
    try {
      const oVch = await getByCode(cVoucherCode);
      if (oVch) {
        oVch.vch_ndiscount = +oVch.vch_ndiscount;
      }
      setVoucher(oVch);
    } catch (oError) {
      setInvalidVoucher(true);
      setVoucher({});
      setVoucherCode('');
    }
    setIsSubmitting(false);
  }


  /**
   * Abre o próximo formulário para editar dados de cobrança
   */
  const handleSubmit = (evtSubmit) => {
    evtSubmit.preventDefault();
    if (bIsSubmitting) return;
    if (oCustomer.wlpn_nid) {
      // não permite alterar dados de cobrança para cliente WL
      setOpenModal('confirmChanges');
    } else {
      setOpenModal('editFinancial');
    }
  };

  const handleClose = (evtClick) => {
    fnOnClose();
  };

  const hireModule = (nModId) => {
    setHiredModules(aPrev => aPrev.includes(nModId) ? aPrev : [...aPrev, nModId]);
  };

  const unhireModule = (nModId) => {
    setHiredModules(aPrev => aPrev.filter(nId => nId !== nModId));
  }

  const handleModuleVersionChange = (nModuleId, nVersion) => {
    setModuleVersions(aPrev => aPrev.map((oModuleVersion) => (
      oModuleVersion.mod_nid === nModuleId
        ? { ...oModuleVersion, prod_nversion: nVersion }
        : oModuleVersion
    )));
  }

  const saveModuleVersions = async () => {
    if (bIsSavingModuleVersions) return;

    const aModules = aModuleVersions
      .filter((oModuleVersion) => oModuleVersion.bIsVersionable)
      .map((oModuleVersion) => ({
        mod_nid: oModuleVersion.mod_nid,
        prod_nversion: oModuleVersion.prod_nversion,
      }));

    setIsSavingModuleVersions(true);
    setSuccesses([]);

    try {
      const oResult = await updateCustomerModuleVersions(nCustId, { aModules });
      setModuleVersions(oResult.aModules || []);
      renderSuccess([oResult.cSuccess || 'Versões dos módulos atualizadas com sucesso.']);
    } catch (oError) {
      renderError(oError.aMsgErrors || ['Erro ao salvar versões dos módulos.']);
    } finally {
      setIsSavingModuleVersions(false);
    }
  }

  const formatSLDate = (cDate) => {
    if (!cDate) return '';
    const [cMonth, cDay, cYear] = cDate.split('/');
    return `${cDay}/${cMonth}/${cYear}`;
  }

  const getMbAsGbStr = (nValue) => (nValue / 1000).toFixed(2);
  const getPriceStr = (nValue) => (nValue / 100).toFixed(2).replace('.', ',');
  const getNextPaymentDateStr = () => {
    if (oBill.oBillNext) return formatSLDate(oBill.oBillNext.due_date);
    if (oBill.nDueDay) return (new Date(oBill.nDueDay)).toLocaleDateString();
    return 'N/A';
  }
  const getPaymentMethodStr = () => {
    const cPaymentMethod = (oBill.oBillNext && oBill.oBillNext.payment_method_name)
      || (oBill.oInvoiceLast && oBill.oInvoiceLast.payment_method_name);
    if (!cPaymentMethod) return 'N/A';
    return cPaymentMethod === 'Boleto' ? cPaymentMethod : 'Cartão';
  }

  const getSaveBtnText = useCallback(() => {
    if (!oCustomer.wlpn_nid) return 'Próximo';
    if (oCustomer.bIsTrial) return 'Gerar plano';
    return 'Alterar plano';
  }, [oCustomer]);

  return (
    <PageClean bLoading={bIsLoading}>
      {cOpenModal === 'editFinancial' && (
        <ModalFinancial
          oCustomer={oCustomer}
          onClose={() => setOpenModal('')}
          onSubmit={savePlan}
        />
      )}
      {cOpenModal === 'confirmChanges' && (
        <ModalConfirm
          oCustomer={oCustomer}
          onConfirm={savePlan}
          onCancel={() => setOpenModal('')}
        />
      )}
      <form autoComplete="false" onSubmit={handleSubmit} className="form" style={{ paddingRight: '10px' }}>
          {aErrors.map((cError) => (
            <Alert
              key={cError}
              type="error"
              onCloseAlert={() => clearError(cError)}
            >
              {cError}
            </Alert>
          ))}
          {aSuccesses.map((cSuccess) => (
            <Alert
              key={cSuccess}
              type="success"
              onCloseAlert={() => clearSuccess(cSuccess)}
            >
              {cSuccess}
            </Alert>
          ))}
          <fieldset>
            <legend>Faturamento</legend>

            <label className="split2">
              <div style={{ display: 'flex' }}>
                <div>
                  Empresa:
                </div>
                <div className="abbreviate-text" style={{ marginLeft: '5px' }} title={oCustomer.cust_cname}>
                  {oCustomer.cust_cname}
                </div>
              </div>
              <p style={{ marginTop: '5px' }}>
                Trial: {oCustomer.bIsTrial ? "Sim" : "Não"}
              </p>
              <p style={{ marginTop: '5px' }}>
                White label: {oCustomer.wlpn_nid ? "Sim" : "Não"}
              </p>
              <div className="button-stack" style={{ textAlign: 'left' }}>
                <a
                  href={oBill.cSuperlogicaAreaLink}
                  className={`btn alternate ${!oBill.cSuperlogicaAreaLink ? 'disabled' : ''}`}
                >
                  Acessar Superlógica
                </a>

                <a
                  href={oBill.cSuperlogicaHistoryLink}
                  className={`btn alternate ${!oBill.cSuperlogicaHistoryLink ? 'disabled' : ''}`}
                >
                  Histórico de faturas
                </a>
              </div>
            </label>

            <label className="split2">
              País de faturamento: {oCustomer.cust_ccountry}
            </label>
            <label className="split2">
              Método de pagamento: {getPaymentMethodStr()}
            </label>
            <label className="split2">
              Último valor pago: {oBill.oInvoiceLast && oBill.oInvoiceLast.total_value != null
                ? `R$${oBill.oInvoiceLast.total_value}`
                : 'N/A'}
            </label>
            <label className="split2">
              Último pagamento: {oBill.oInvoiceLast ? `${oBill.oInvoiceLast.id + ' em ' + formatSLDate(oBill.oInvoiceLast.payment_date)}` : 'N/A'}
            </label>
            <label className="split2">
              Próximo pagamento em: {getNextPaymentDateStr()}
            </label>

          </fieldset>


          <fieldset>
            <legend>Plano</legend>

            <label className="split2">
              Valor {bIsAnnualPlan ? "anual" : "mensal"}: R${getPriceStr(nPrice)} {!!oVoucher.vch_ccode && `(${oVoucher.vch_ccode})`}
            </label>
            <AltCheckbox
              checked={bIsAnnualPlan}
              onChange={() => setIsAnnualPlan(!bIsAnnualPlan)}
              name="bIsAnnual"
              label="Anual"
              style={{ paddingTop: 0}}
            />

            <label className="split2">
              Usuários ativos: {oCustomer.nUserCount || 0}
            </label>
            <label className="split2">
              Armazenamento em uso: {getMbAsGbStr(oCustomer.cust_nused_space_quota_mb)}GB
            </label>

            <label className="split2" htmlFor="nHiredUsers">
              Usuários contratados:
              <Input
                type="number"
                name="nHiredUsers"
                className={oClasses.numberInput}
                required
                onChange={(evtChange) => setHiredUsers(+evtChange.target.value)}
                value={nHiredUsers}
                min={nMinUsers || 0}
              />
            </label>
            <label className="split2" htmlFor="nHiredStorage">
              Armazenamento contratado:
              <Input
                type="number"
                name="nHiredStorage"
                className={oClasses.numberInput}
                required
                onChange={(evtChange) => setHiredStorage(+evtChange.target.value)}
                value={nHiredStorage}
                min={nMinSpace || 0}
              />
            </label>

            <div>
              <div style={{ marginBottom: '20px', fontWeight: 'bold' }}>
                Módulos:
              </div>
              {aSysModules.map(({mod_nid, mod_cname}) => (
                  <AltCheckbox
                    key={mod_cname}
                    className="split1"
                    name={mod_cname}
                    label={mod_cname}
                    onChange={(evtChange) => evtChange.target.checked ? hireModule(mod_nid) : unhireModule(mod_nid)}
                    checked={aHiredModules.includes(mod_nid)}
                  />
                ))}
            </div>
          </fieldset>

          <fieldset>
            <legend>Versões dos módulos</legend>

            <p style={{ marginBottom: '20px' }}>
              Configure a versão liberada para os módulos contratados. A alteração passa a valer para os usuários após novo login.
            </p>

            {aModuleVersions.length === 0 && (
              <label className="split1">
                Nenhum módulo contratado para este cliente.
              </label>
            )}

            {aModuleVersions.map((oModuleVersion) => (
              <label className="split2" key={oModuleVersion.prod_nid} htmlFor={`module-version-${oModuleVersion.mod_nid}`}>
                {oModuleVersion.mod_cname}:
                <Input
                  type="select"
                  name={`module-version-${oModuleVersion.mod_nid}`}
                  id={`module-version-${oModuleVersion.mod_nid}`}
                  value={oModuleVersion.prod_nversion || 1}
                  disabled={!oModuleVersion.bIsVersionable}
                  onChange={(evtChange) => handleModuleVersionChange(oModuleVersion.mod_nid, +evtChange.target.value)}
                >
                  {(oModuleVersion.aVersionOptions || [1]).map((nVersion) => (
                    <option key={nVersion} value={nVersion}>
                      Versão {nVersion}
                    </option>
                  ))}
                </Input>
                {!oModuleVersion.bIsVersionable && (
                  <small>Este módulo ainda não possui controle manual de versão.</small>
                )}
              </label>
            ))}

            <div className="button-stack">
              <button
                type="button"
                className={`btn btn-secondary${(!bHasVersionableModules || bIsSavingModuleVersions) ? ' disabled' : ''}`}
                title="Salvar versões"
                disabled={!bHasVersionableModules || bIsSavingModuleVersions}
                onClick={saveModuleVersions}
              >
                {bIsSavingModuleVersions ? 'Salvando...' : 'Salvar versões'}
              </button>
            </div>
          </fieldset>

          <fieldset>
            <legend>Voucher</legend>

            {bInvalidVoucher && (
              <Alert
                type="error"
                isOpen={true}
                onCloseAlert={() => setInvalidVoucher(false)}
              >
                Voucher inválido
              </Alert>
            )}

            <label className="split3 colspan">
              <Input
                type="text"
                name="vch_ccode"
                onChange={handleVoucherChange}
                value={cVoucherCode || ''}
              />
            </label>
            <button
              type="button"
              className="btn btn-secondary split3"
              title="Aplicar"
              onClick={verifyVoucher}
              style={{ height: "40px", lineHeight: "40px", marginTop: "5px" }}
            >
              Aplicar
            </button>
          </fieldset>

          <div className="button-stack">
            <button
              type="button"
              className="btn alternate back btn btn-outline-secondary" title="Cancelar"
              onClick={handleClose}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className={`btn btn-secondary${bDisableHire ? ' disabled' : ''}`}
              title={getSaveBtnText()}
              disabled={bDisableHire}
            >
              {getSaveBtnText()}
            </button>
          </div>
        </form>
    </PageClean>
  );
};

const ModalFinancial = ({ oCustomer, onClose, onSubmit }) => {
  return (
    <SweetAlert customClass="modal-edit lg" title="" onConfirm={onClose} showConfirm={false}>
      <div className="head-modal">
        Cobrança
        <span role="button" tabIndex="0" onClick={onClose} className="close" />
      </div>
      <CustomerFinancial oCustomer={oCustomer} onClose={onClose} onSubmit={onSubmit} />
    </SweetAlert>
  );
};

const ModalConfirm = ({ oCustomer, onConfirm, onCancel}) => {
  return (
    <SweetAlert
      warning
      showCancel
      confirmBtnText={'Confirma'}
      cancelBtnText={'Cancelar'}
      cancelBtnBsStyle="danger"
      title={''}
      onConfirm={() => onConfirm(oCustomer)}
      onCancel={onCancel}
    >
      <p>Deseja realmente {oCustomer.bIsTrial ? 'gerar' : 'alterar'} o plano do cliente white label: {oCustomer.cust_cname}?</p>
    </SweetAlert>
  );
}

export default CustomerPlanModal;
